<script setup>
import {computed, onMounted, reactive, ref} from 'vue';

const props = defineProps({
    channelId: {
        type: Number,
        required: true,
    },
});

const reporting = reactive({
    channel: {},
    summary: {
        total: 0,
        active: 0,
        passive: 0,
        inactive: 0,
    },
    members: [],
});

const loading = ref(true);
const search = ref('');
const statusFilter = ref('all');
const sort = reactive({
    key: 'name',
    direction: 'asc',
});

const statusOptions = ['all', 'active', 'passive', 'inactive'];
const statusSortOrder = {
    active: 1,
    passive: 2,
    inactive: 3,
};

const loadOverview = async () => {
    loading.value = true;

    const res = await axios.get(route('community.admin.api.reporting.channel-overview', props.channelId));

    reporting.channel = res.data.channel;
    reporting.summary = res.data.summary;
    reporting.members = res.data.members;
    loading.value = false;
};

const filteredMembers = computed(() => {
    const query = search.value.toLowerCase();

    return reporting.members.filter((member) => {
        const matchesSearch =
            member.name.toLowerCase().includes(query) ||
            member.email.toLowerCase().includes(query);
        const matchesStatus =
            statusFilter.value === 'all' ||
            member.status.key === statusFilter.value;

        return matchesSearch && matchesStatus;
    }).sort((memberA, memberB) => compareMembers(memberA, memberB, sort.key, sort.direction));
});

const sortBy = (key) => {
    if (sort.key === key) {
        sort.direction = sort.direction === 'asc' ? 'desc' : 'asc';

        return;
    }

    sort.key = key;
    sort.direction = defaultSortDirection(key);
};

const defaultSortDirection = (key) => {
    if (key === 'average_rating_7_days' || key === 'average_rating_30_days') {
        return 'desc';
    }

    return 'asc';
};

const sortIndicator = (key) => {
    if (sort.key !== key) {
        return '';
    }

    return sort.direction === 'asc' ? '↑' : '↓';
};

const compareMembers = (memberA, memberB, key, direction) => {
    if (key === 'name') {
        return compareValues(memberA.name.localeCompare(memberB.name), direction);
    }

    if (key === 'status') {
        return compareValues(
            statusSortOrder[memberA.status.key] - statusSortOrder[memberB.status.key],
            direction,
        );
    }

    if (key === 'last_check_in') {
        return compareNullableNumbers(
            memberA.days_since_last_check_in,
            memberB.days_since_last_check_in,
            direction,
        );
    }

    if (key === 'average_rating_7_days') {
        return compareNullableNumbers(
            memberA.average_rating_7_days,
            memberB.average_rating_7_days,
            direction,
        );
    }

    if (key === 'average_rating_30_days') {
        return compareNullableNumbers(
            memberA.average_rating_30_days,
            memberB.average_rating_30_days,
            direction,
        );
    }

    return 0;
};

const compareValues = (comparison, direction) => {
    return direction === 'asc' ? comparison : comparison * -1;
};

const compareNullableNumbers = (valueA, valueB, direction) => {
    const aIsNull = valueA === null || valueA === undefined;
    const bIsNull = valueB === null || valueB === undefined;

    if (aIsNull && bIsNull) {
        return 0;
    }

    if (aIsNull) {
        return 1;
    }

    if (bIsNull) {
        return -1;
    }

    return compareValues(Number(valueA) - Number(valueB), direction);
};

const statusLabel = (status) => {
    return _mft(`community:reporting.overview.status.${status}`);
};

const statusClass = (status) => {
    return {
        active: 'bg-green-100 text-green-800',
        passive: 'bg-yellow-100 text-yellow-800',
        inactive: 'bg-red-100 text-red-800',
    }[status];
};

const statusDotClass = (status) => {
    return {
        active: 'bg-green-500',
        passive: 'bg-yellow-500',
        inactive: 'bg-red-500',
    }[status];
};

const formatAverage = (value) => {
    if (value === null || value === undefined) {
        return '—';
    }

    return Number(value).toFixed(1);
};

const formatLastCheckIn = (member) => {
    if (member.days_since_last_check_in === null) {
        return _mft('community:reporting.overview.never');
    }

    if (member.days_since_last_check_in === 0) {
        return _mft('community:reporting.overview.today');
    }

    if (member.days_since_last_check_in === 1) {
        return _mft('community:reporting.overview.yesterday');
    }

    return _mft('community:reporting.overview.daysAgo', {
        count: member.days_since_last_check_in,
    });
};

const trendBarClass = (average) => {
    if (average === null || average === undefined) {
        return 'bg-gray-200';
    }

    if (average < 3) {
        return 'bg-red-500';
    }

    if (average < 4) {
        return 'bg-yellow-500';
    }

    return 'bg-green-500';
};

const trendBarStyle = (average) => {
    if (average === null || average === undefined) {
        return {height: '4px'};
    }

    const normalizedAverage = Math.max(1, Math.min(5, Number(average)));

    return {
        height: `${Math.round((normalizedAverage / 5) * 32)}px`,
    };
};

const trendBarTitle = (bucket) => {
    return `${bucket.start_date} - ${bucket.end_date}: ${formatAverage(bucket.average)}`;
};

onMounted(() => {
    loadOverview();
});
</script>

<template>
    <div>
        <frosted-bar>
            <template #navigation>
                <a :href="route('community.admin.reporting.index')" class="mf-link">
                    {{ _mft('community:reporting.channels.title') }}
                </a>
                <span class="px-2 text-gray-300">/</span>
                <a :href="route('community.admin.reporting.channel', props.channelId)" class="mf-link">
                    {{ _mft('community:reporting.members.title') }}
                </a>
            </template>
            <div class="flex items-center justify-between space-x-4">
                <h1>
                    {{ reporting.channel.name ? reporting.channel.name : _mft('community:reporting.overview.title') }}
                </h1>
            </div>
        </frosted-bar>

        <div class="mt-6 space-y-6">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
                <card-basic class="!mb-0" compact>
                    <template #body>
                        <div class="text-2xl font-semibold">{{ reporting.summary.total }}</div>
                        <div class="text-xs text-gray-500">{{ _mft('community:reporting.overview.total') }}</div>
                    </template>
                </card-basic>

                <card-basic class="!mb-0 border-l-4 border-green-500" compact>
                    <template #body>
                        <div class="text-2xl font-semibold text-green-700">{{ reporting.summary.active }}</div>
                        <div class="text-xs text-gray-500">{{ _mft('community:reporting.overview.activeLast7Days') }}</div>
                    </template>
                </card-basic>

                <card-basic class="!mb-0 border-l-4 border-yellow-500" compact>
                    <template #body>
                        <div class="text-2xl font-semibold text-yellow-700">{{ reporting.summary.passive }}</div>
                        <div class="text-xs text-gray-500">{{ _mft('community:reporting.overview.passive8To14Days') }}</div>
                    </template>
                </card-basic>

                <card-basic class="!mb-0 border-l-4 border-red-500" compact>
                    <template #body>
                        <div class="text-2xl font-semibold text-red-700">{{ reporting.summary.inactive }}</div>
                        <div class="text-xs text-gray-500">{{ _mft('community:reporting.overview.inactive15Days') }}</div>
                    </template>
                </card-basic>
            </div>

            <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                    <h2 class="text-lg font-semibold">
                        {{ _mft('community:reporting.overview.title') }}
                    </h2>
                </div>

                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input-text
                        v-model:content="search"
                        :placeholder="_mft('shared:search')"
                        class="w-full sm:w-64 !mb-0"
                    ></input-text>

                    <select
                        v-model="statusFilter"
                        class="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
                    >
                        <option
                            v-for="status in statusOptions"
                            :key="status"
                            :value="status"
                        >
                            {{ status === 'all' ? _mft('community:reporting.overview.status.all') : statusLabel(status) }}
                        </option>
                    </select>
                </div>
            </div>

            <card-basic
                class="!mb-0 overflow-hidden !p-0"
                compact
            >
                <template #body>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 text-sm">
                            <thead class="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <tr>
                                    <th class="px-4 py-3">
                                        <button class="font-semibold uppercase" type="button" @click="sortBy('name')">
                                            {{ _mft('community:reporting.overview.member') }} {{ sortIndicator('name') }}
                                        </button>
                                    </th>
                                    <th class="px-4 py-3">
                                        <button class="font-semibold uppercase" type="button" @click="sortBy('status')">
                                            {{ _mft('community:reporting.overview.status') }} {{ sortIndicator('status') }}
                                        </button>
                                    </th>
                                    <th class="px-4 py-3">
                                        <button class="font-semibold uppercase" type="button" @click="sortBy('last_check_in')">
                                            {{ _mft('community:reporting.overview.latestCheckIn') }} {{ sortIndicator('last_check_in') }}
                                        </button>
                                    </th>
                                    <th class="px-4 py-3 text-right">
                                        <button class="font-semibold uppercase" type="button" @click="sortBy('average_rating_7_days')">
                                            {{ _mft('community:reporting.overview.average7Days') }} {{ sortIndicator('average_rating_7_days') }}
                                        </button>
                                    </th>
                                    <th class="px-4 py-3 text-right">
                                        <button class="font-semibold uppercase" type="button" @click="sortBy('average_rating_30_days')">
                                            {{ _mft('community:reporting.overview.average30Days') }} {{ sortIndicator('average_rating_30_days') }}
                                        </button>
                                    </th>
                                    <th class="px-4 py-3">{{ _mft('community:reporting.overview.trend30Days') }}</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100 bg-white">
                                <tr v-if="loading">
                                    <td class="px-4 py-6 text-center text-gray-400" colspan="6">
                                        {{ _mft('shared:loading') }}
                                    </td>
                                </tr>
                                <tr
                                    v-for="member in filteredMembers"
                                    v-else
                                    :key="member.id"
                                    class="hover:bg-gray-50"
                                >
                                    <td class="px-4 py-3">
                                        <a
                                            class="mf-link font-medium"
                                            :href="route('community.admin.reporting.member', [props.channelId, member.id])"
                                        >
                                            {{ member.name }}
                                        </a>
                                        <div class="text-xs text-gray-400">{{ member.email }}</div>
                                    </td>
                                    <td class="px-4 py-3">
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                                            :class="statusClass(member.status.key)"
                                        >
                                            <span class="mr-1.5 h-2 w-2 rounded-full" :class="statusDotClass(member.status.key)"></span>
                                            {{ statusLabel(member.status.key) }}
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 text-gray-700">
                                        {{ formatLastCheckIn(member) }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-medium text-gray-900">
                                        {{ formatAverage(member.average_rating_7_days) }}
                                    </td>
                                    <td class="px-4 py-3 text-right font-medium text-gray-900">
                                        {{ formatAverage(member.average_rating_30_days) }}
                                    </td>
                                    <td class="px-4 py-3">
                                        <div
                                            class="flex h-10 w-20 items-end gap-1"
                                            :aria-label="_mft('community:reporting.overview.trend30Days')"
                                        >
                                            <span
                                                v-for="(bucket, index) in member.rating_trend_30_days"
                                                :key="index"
                                                class="block w-4 rounded-sm"
                                                :class="trendBarClass(bucket.average)"
                                                :style="trendBarStyle(bucket.average)"
                                                :title="trendBarTitle(bucket)"
                                            ></span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="!loading && filteredMembers.length === 0">
                                    <td class="px-4 py-6 text-center text-gray-400" colspan="6">
                                        {{ _mft('community:reporting.overview.noMembers') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </card-basic>
        </div>
    </div>
</template>
